import { AfterViewInit, Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { skip } from 'rxjs';

import { AccountPageService } from 'src/app/features/user-account/services/account-page.service';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { CLAIM_PAGE } from 'src/app/infrastructure/injection-tokens/PAGE-NAME';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { sectionLayout, TSectionLayout } from 'src/app/infrastructure/models/section-layout';

// Edit-sectie van EmailChangeComponent: zelfde opzet als PasswordChangeEditComponent (pagina start in
// edit-modus, standaard save-knop in de footer via _CentralPageService, knop-claims van Pages.EmailChange).
@Component({
    selector: 'app-email-change-edit',
    templateUrl: './email-change-edit.component.html',
    standalone: true,
    imports: [
        SharedModule,
        InputControlComponent,
    ],
    styleUrls: [
        '../../../account.scss',
        './email-change-edit.component.scss',
    ]
})
export class EmailChangeEditComponent
    extends _SectionBaseComponent
    implements OnInit, AfterViewInit
{
    private readonly __accountPageService = injectRequired(AccountPageService);
    private readonly __applicationUserService = injectRequired(ApplicationUserService);
    private readonly __authService = injectRequired(AuthService);
    private readonly __claimPage = inject(CLAIM_PAGE, { optional: true });
    private readonly __formBuilder = injectRequired(UntypedFormBuilder);
    private readonly __notificationService = injectRequired(NotificationService);

    protected override readonly _centralPageService = injectRequired(_CentralPageService);
    protected override readonly _footerService = injectRequired(FooterService);
    protected override readonly _headerService = injectRequired(HeaderService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _pageService = injectRequired(_PageService);

    override out_onEscape = this._out_onEscape;
    override out_onPageLoaded = this._out_onPageLoaded;

    override readonly sectionLayout$$ = signal<TSectionLayout>(sectionLayout.form);

    override template_footerLeft$$ = this._template_footerLeft$$;
    override template_sectionDetails_extraButtons$$ = this._template_sectionDetails_extraButtons$$;
    override template_sectionEdit_extraButtons$$ = this._template_sectionEdit_extraButtons$$;

    override footerData: Partial<IFooterData> = {
        hideSideBlockLeft: false,
        hideSideBlockRight: false,
    };
    override headerData: Partial<IHeaderData> = {
        title: 'e-mailadres wijzigen',
    };

    private __form: UntypedFormGroup = this.__formBuilder.group({
        newEmail: ['', [Validators.required, Validators.email]],
    });

    get Form() { return this.__form; }
    // Cast naar FormControl: UntypedFormGroup.controls levert AbstractControl, maar
    // app-input-control verwacht een FormControl-input (runtime zijn het FormControls).
    get FormControls() { return this.__form.controls as { [key: string]: FormControl }; }

    currentEmail$$ = this.__applicationUserService.accountEmail$$;

    showErrors = false;

    constructor() {
        super();

        // Save-knop pas actief als aan de validatie is voldaan (zelfde regel als FormComponent).
        this.__form.statusChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => this._centralPageService.btnSaveData_set({ disabled: this.__form.invalid }));

        // Footer-knoppen. skip(1): de streams zijn BehaviorSubjects en spelen bij subscribe de laatste
        // waarde opnieuw af (zelfde reden als het __initialize-filter in _SectionEditComponent).
        this._centralPageService.onSaveEmit$
            .pipe(skip(1), takeUntilDestroyed())
            .subscribe(() => this.emit_submit());
        this._centralPageService.onCancelEmit$
            .pipe(skip(1), takeUntilDestroyed())
            .subscribe(() => this.emit_onEscape());
    }

    override ngOnInit(): void {
        super._ngOnInit();
        // Knop-zichtbaarheid via de claims van deze pagina; save disabled tot het formulier geldig is.
        if (this.__claimPage) this._centralPageService.pageName_sectionEdit_set(this.__claimPage);
        this._centralPageService.btnSaveData_set({ disabled: true });

        // De claims van deze pagina zijn al binnen via de route-resolver (Page_Read-poort).
        this.emit_pageLoaded();
    }

    override emit_onEscape(): void {
        super._emit_onEscape();
    }

    override emit_pageLoaded(): void {
        super._emit_pageLoaded();
    }

    emit_submit(): void {
        // Enter in het formulier volgt de save-knop: zonder knop (geen BtnPageSave_Show) geen opslaan.
        if (this._centralPageService.btnSaveData$$().hide) return;
        if (this.__form.invalid) {
            this.showErrors = true;
            return;
        }

        const __newEmail: string = this.__form.value.newEmail;

        this.__authService.postChangeEmail$(__newEmail)
            .subscribe({
                next: () => {
                    // Geen vaste duur (0): de gebruiker moet dit kunnen lezen en wegklikken.
                    this.__notificationService.pushNotification({
                        severity: 'info',
                        title: 'Controleer je e-mail',
                        text: `We hebben een bevestigingslink gestuurd naar ${__newEmail}. Bevestig je nieuwe e-mailadres via de link en log daarna in met je nieuwe e-mailadres.`,
                        duration: 0,
                    });
                    // Uitloggen i.p.v. terug: de sessie hoort bij het oude adres; na bevestigen logt
                    // de gebruiker opnieuw in met het nieuwe adres.
                    this.__accountPageService.logOut();
                },
                // Adres al in gebruik / mail-fout: centraal gerapporteerd, form blijft staan.
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }
}
